import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { HYROX_TRAININGEN } from "../data/schemaDataHyrox";
import {
  ArrowLeft, Play, Pause, Square, Volume2, VolumeX,
  Flag, RotateCcw, ChevronRight, Timer, Zap, Target, Clock, X,
  CheckCircle, SkipForward
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_fitsmith-academy/artifacts/zbh0gz3y_291E0B11-36FD-4420-BAF1-DDEC0FEFC105.png";

// ─── HELPERS ─────────────────────────────────────────────────────────────────
function formatTime(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function playBeep(type = "beep", enabled = true) {
  if (!enabled) return;
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (type === "done") {
      [0, 0.28, 0.56].forEach((delay, i) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.frequency.value = [440, 660, 880][i];
        g.gain.setValueAtTime(0.5, ctx.currentTime + delay);
        g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + 0.25);
        o.start(ctx.currentTime + delay);
        o.stop(ctx.currentTime + delay + 0.25);
      });
    } else {
      const freq = { start: 880, rest: 660, next: 770, stop: 440 }[type] || 660;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.5, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.3);
    }
  } catch (_) {}
}

// ─── TYPE BADGE ─────────────────────────────────────────────────────────────
function TypeBadge({ type, kleur }) {
  const labels = { afstand: "Afstand", reps: "Reps", calorie: "Calorieën", tijd: "Tijd" };
  return (
    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
      style={{ background: `${kleur}20`, color: kleur, border: `1px solid ${kleur}40` }}>
      {labels[type] || type}
    </span>
  );
}

// ─── HYROX TIMER OVERLAY ──────────────────────────────────────────────────────
function HyroxTimerOverlay({ training, onClose }) {
  const [fase, setFase] = useState("idle");
  const [totalTijd, setTotalTijd] = useState(0);
  const [restTijd, setRestTijd] = useState(0);
  const [huidigeRonde, setHuidigeRonde] = useState(1);
  const [geluidAan, setGeluidAan] = useState(true);
  const [splits, setSplits] = useState([]);
  const [splitIdx, setSplitIdx] = useState(0);

  const geluidRef = useRef(true);
  useEffect(() => { geluidRef.current = geluidAan; }, [geluidAan]);

  // Total time counting up during 'werk'
  useEffect(() => {
    if (fase !== "werk") return;
    const iv = setInterval(() => setTotalTijd(t => t + 1), 1000);
    return () => clearInterval(iv);
  }, [fase]);

  // Rest countdown
  useEffect(() => {
    if (fase !== "rust") return;
    const iv = setInterval(() => {
      setRestTijd(r => {
        if (r <= 1) {
          playBeep("start", geluidRef.current);
          setFase("werk");
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [fase]);

  const handleStart = useCallback(() => {
    playBeep("start", geluidRef.current);
    setFase("werk");
    setTotalTijd(0);
    setHuidigeRonde(1);
    setSplits([]);
    setSplitIdx(0);
  }, []);

  const handlePauseer = useCallback(() => {
    setFase(f => f === "werk" ? "gepauzeerd" : "werk");
  }, []);

  const handleVolgendeRonde = useCallback(() => {
    if (huidigeRonde >= training.rondes) {
      playBeep("done", geluidRef.current);
      setFase("klaar");
    } else {
      setHuidigeRonde(r => r + 1);
      if (training.rustTijd > 0) {
        setRestTijd(training.rustTijd);
        setFase("rust");
        playBeep("rest", geluidRef.current);
      } else {
        playBeep("next", geluidRef.current);
      }
    }
  }, [huidigeRonde, training.rondes, training.rustTijd]);

  const handleSlaRustOver = useCallback(() => {
    playBeep("start", geluidRef.current);
    setFase("werk");
  }, []);

  const handleSplit = useCallback(() => {
    const ex = training.exercises[splitIdx];
    const newSplit = { naam: ex.naam, doel: ex.doel_waarde, tijd: totalTijd };
    setSplits(s => [...s, newSplit]);
    if (splitIdx >= training.exercises.length - 1) {
      playBeep("done", geluidRef.current);
      setFase("klaar");
    } else {
      setSplitIdx(i => i + 1);
      playBeep("next", geluidRef.current);
    }
  }, [splitIdx, training.exercises, totalTijd]);

  const handleReset = useCallback(() => {
    setFase("idle");
    setTotalTijd(0);
    setRestTijd(0);
    setHuidigeRonde(1);
    setSplits([]);
    setSplitIdx(0);
  }, []);

  const isRaceMode = training.timerModus === "race";
  const isKlaar = fase === "klaar";
  const isIdle = fase === "idle";
  const isRust = fase === "rust";
  const isWerk = fase === "werk" || fase === "gepauzeerd";

  return (
    <div className="fixed inset-0 z-50 flex flex-col" style={{ background: "#050505" }}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/8">
        <div>
          <p className="text-xs font-black uppercase tracking-widest" style={{ color: training.kleur }}>
            Hyrox Timer
          </p>
          <p className="text-white font-bold text-base leading-tight">{training.naam}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="hyrox-sound-btn"
            onClick={() => setGeluidAan(s => !s)}
            className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
          >
            {geluidAan ? <Volume2 size={16} /> : <VolumeX size={16} />}
          </button>
          <button
            onClick={onClose}
            data-testid="hyrox-timer-close"
            className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-zinc-400 hover:text-red-400 transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Main timer area */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 overflow-y-auto">

        {/* Phase indicator */}
        <div className="mb-4 text-center">
          {isIdle && (
            <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-5 py-2">
              <Timer size={14} style={{ color: training.kleur }} />
              <span className="text-sm font-bold text-white">Klaar om te starten</span>
            </div>
          )}
          {isRust && (
            <div className="flex items-center gap-2 bg-yellow-400/10 border border-yellow-400/25 rounded-full px-5 py-2">
              <Clock size={14} className="text-yellow-400" />
              <span className="text-sm font-bold text-yellow-400">Rusttijd</span>
            </div>
          )}
          {isWerk && !isRaceMode && (
            <div className="flex items-center gap-2 rounded-full px-5 py-2 border"
              style={{ background: `${training.kleur}15`, borderColor: `${training.kleur}40` }}>
              <Zap size={14} style={{ color: training.kleur }} />
              <span className="text-sm font-bold" style={{ color: training.kleur }}>
                Ronde {huidigeRonde} / {training.rondes}
              </span>
            </div>
          )}
          {isWerk && isRaceMode && (
            <div className="flex items-center gap-2 rounded-full px-5 py-2 border"
              style={{ background: `${training.kleur}15`, borderColor: `${training.kleur}40` }}>
              <Flag size={14} style={{ color: training.kleur }} />
              <span className="text-sm font-bold" style={{ color: training.kleur }}>
                Race Modus — {splitIdx + 1} / {training.exercises.length}
              </span>
            </div>
          )}
          {isKlaar && (
            <div className="flex items-center gap-2 bg-green-400/10 border border-green-400/25 rounded-full px-5 py-2">
              <CheckCircle size={14} className="text-green-400" />
              <span className="text-sm font-bold text-green-400">Training voltooid!</span>
            </div>
          )}
        </div>

        {/* Main time display */}
        <div className="text-center mb-6">
          {isRust ? (
            <>
              <div
                data-testid="hyrox-rest-timer"
                className="font-mono font-black leading-none text-yellow-400"
                style={{ fontSize: "clamp(4rem, 18vw, 8rem)" }}
              >
                {formatTime(restTijd)}
              </div>
              <p className="text-zinc-500 text-sm mt-2">Rust resterend</p>
            </>
          ) : (
            <>
              <div
                data-testid="hyrox-main-timer"
                className="font-mono font-black leading-none text-white"
                style={{
                  fontSize: "clamp(4rem, 18vw, 8rem)",
                  color: isKlaar ? "#4ade80" : "white",
                }}
              >
                {formatTime(totalTijd)}
              </div>
              <p className="text-zinc-500 text-sm mt-2">
                {isIdle ? "Totale tijd" : isKlaar ? "Eindtijd" : "Totale tijd"}
              </p>
            </>
          )}
        </div>

        {/* Race mode: current exercise */}
        {isRaceMode && !isIdle && !isKlaar && (
          <div className="mb-6 text-center w-full max-w-sm">
            <div className="rounded-2xl p-4 border" style={{ background: `${training.kleur}10`, borderColor: `${training.kleur}30` }}>
              <p className="text-zinc-400 text-xs uppercase tracking-wider mb-1 font-bold">Huidige oefening</p>
              <p className="text-white font-black text-2xl">{training.exercises[splitIdx]?.naam}</p>
              <p className="mt-1 text-sm font-semibold" style={{ color: training.kleur }}>
                {training.exercises[splitIdx]?.doel_waarde}
              </p>
              {splitIdx < training.exercises.length - 1 && (
                <p className="text-zinc-600 text-xs mt-2 flex items-center justify-center gap-1">
                  <ChevronRight size={11} />
                  Volgende: {training.exercises[splitIdx + 1]?.naam}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Round mode: exercise list compact */}
        {!isRaceMode && isWerk && (
          <div className="mb-6 w-full max-w-sm">
            <p className="text-zinc-600 text-xs uppercase tracking-wider mb-2 font-bold text-center">Oefeningen deze ronde</p>
            <div className="space-y-1.5">
              {training.exercises.map((ex, i) => (
                <div key={ex.id} className="flex items-center gap-3 bg-white/4 border border-white/6 rounded-xl px-3 py-2">
                  <span className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-black shrink-0"
                    style={{ background: `${training.kleur}20`, color: training.kleur }}>
                    {i + 1}
                  </span>
                  <span className="text-white text-sm font-semibold flex-1 truncate">{ex.naam}</span>
                  <span className="text-zinc-500 text-xs shrink-0">{ex.doel_waarde}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Splits list (race mode) */}
        {splits.length > 0 && (
          <div className="mb-6 w-full max-w-sm">
            <p className="text-zinc-600 text-xs uppercase tracking-wider mb-2 font-bold">Split tijden</p>
            <div className="space-y-1.5">
              {splits.map((s, i) => (
                <div key={i} className="flex items-center justify-between bg-white/4 border border-white/6 rounded-xl px-4 py-2.5">
                  <div className="flex items-center gap-2 min-w-0">
                    <Flag size={11} style={{ color: training.kleur }} className="shrink-0" />
                    <span className="text-zinc-300 text-sm truncate">{s.naam}</span>
                  </div>
                  <span className="text-white font-mono font-bold text-sm ml-2 shrink-0">{formatTime(s.tijd)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Completion stats */}
        {isKlaar && (
          <div className="mb-6 w-full max-w-sm">
            <div className="rounded-2xl p-5 border text-center"
              style={{ background: `${training.kleur}08`, borderColor: `${training.kleur}30` }}>
              <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1 font-bold">Eindtijd</p>
              <p className="font-mono font-black text-4xl text-white mb-1">{formatTime(totalTijd)}</p>
              {!isRaceMode && (
                <p className="text-zinc-400 text-sm">{training.rondes} rondes voltooid</p>
              )}
              {isRaceMode && (
                <p className="text-zinc-400 text-sm">{splits.length} onderdelen geregistreerd</p>
              )}
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="w-full max-w-sm space-y-3 pb-6">
          {isIdle && (
            <button
              data-testid="hyrox-start-btn"
              onClick={handleStart}
              className="w-full py-5 rounded-2xl font-black text-xl text-black uppercase tracking-wider flex items-center justify-center gap-3 transition-transform active:scale-95"
              style={{ background: training.kleur }}
            >
              <Play size={22} fill="black" /> Start Training
            </button>
          )}

          {isWerk && (
            <div className="flex gap-3">
              <button
                data-testid="hyrox-pause-btn"
                onClick={handlePauseer}
                className="flex-1 py-4 rounded-2xl font-bold text-base border border-white/15 text-white flex items-center justify-center gap-2 hover:bg-white/5 transition-colors"
              >
                {fase === "gepauzeerd" ? <><Play size={16} /> Hervat</> : <><Pause size={16} /> Pauze</>}
              </button>
              {isRaceMode ? (
                <button
                  data-testid="hyrox-split-btn"
                  onClick={handleSplit}
                  className="flex-1 py-4 rounded-2xl font-bold text-base text-black flex items-center justify-center gap-2 transition-transform active:scale-95"
                  style={{ background: training.kleur }}
                >
                  <Flag size={16} /> Split
                </button>
              ) : (
                <button
                  data-testid="hyrox-next-round-btn"
                  onClick={handleVolgendeRonde}
                  className="flex-1 py-4 rounded-2xl font-bold text-base text-black flex items-center justify-center gap-2 transition-transform active:scale-95"
                  style={{ background: training.kleur }}
                >
                  {huidigeRonde >= training.rondes
                    ? <><CheckCircle size={16} /> Klaar</>
                    : <><SkipForward size={16} /> Volgende ronde</>
                  }
                </button>
              )}
            </div>
          )}

          {isRust && (
            <div className="flex gap-3">
              <button
                data-testid="hyrox-skip-rest-btn"
                onClick={handleSlaRustOver}
                className="flex-1 py-4 rounded-2xl font-bold text-base border border-yellow-400/40 text-yellow-400 flex items-center justify-center gap-2 hover:bg-yellow-400/5 transition-colors"
              >
                <SkipForward size={16} /> Sla rust over
              </button>
              <button
                data-testid="hyrox-reset-btn"
                onClick={handleReset}
                className="py-4 px-5 rounded-2xl font-bold text-base border border-white/10 text-zinc-500 hover:text-white transition-colors"
              >
                <RotateCcw size={16} />
              </button>
            </div>
          )}

          {isKlaar && (
            <div className="flex gap-3">
              <button
                data-testid="hyrox-again-btn"
                onClick={handleReset}
                className="flex-1 py-4 rounded-2xl font-bold text-base border border-white/15 text-white flex items-center justify-center gap-2 hover:bg-white/5 transition-colors"
              >
                <RotateCcw size={16} /> Opnieuw
              </button>
              <button
                onClick={onClose}
                className="flex-1 py-4 rounded-2xl font-bold text-base text-black flex items-center justify-center gap-2"
                style={{ background: training.kleur }}
              >
                <X size={16} /> Sluiten
              </button>
            </div>
          )}

          {/* Reset during work (small) */}
          {isWerk && (
            <button
              onClick={handleReset}
              className="w-full py-2 text-zinc-600 hover:text-zinc-400 text-xs flex items-center justify-center gap-1 transition-colors"
            >
              <RotateCcw size={11} /> Training resetten
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── EXERCISE CARD ───────────────────────────────────────────────────────────
function HyroxExerciseCard({ exercise, index, kleur }) {
  return (
    <div
      data-testid={`hyrox-exercise-${index}`}
      className="flex gap-4 bg-[#0f0f0f] border border-white/6 rounded-2xl p-4 hover:border-white/12 transition-colors"
    >
      <div
        className="w-9 h-9 rounded-xl flex items-center justify-center font-black text-sm shrink-0"
        style={{ background: `${kleur}18`, color: kleur }}
      >
        {index + 1}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3 mb-1">
          <p className="text-white font-bold text-sm">{exercise.naam}</p>
          <TypeBadge type={exercise.type} kleur={kleur} />
        </div>
        <p className="font-semibold text-sm mb-1.5" style={{ color: kleur }}>
          {exercise.doel_waarde}
        </p>
        {exercise.notitie && (
          <p className="text-zinc-500 text-xs leading-relaxed">{exercise.notitie}</p>
        )}
      </div>
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function HyroxViewer() {
  const { key } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [timerOpen, setTimerOpen] = useState(false);

  const training = HYROX_TRAININGEN.find(t => t.id === key);

  if (!training) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center gap-4">
        <Timer size={40} className="text-zinc-700" />
        <p className="text-zinc-400 text-sm">Training niet gevonden.</p>
        <button
          onClick={() => navigate("/account")}
          className="text-sm text-zinc-500 hover:text-white border border-white/10 px-4 py-2 rounded-xl transition-colors"
        >
          Terug naar account
        </button>
      </div>
    );
  }

  const timerModeLabel = {
    rondes: `${training.rondes} rondes · ${training.rustTijd}s rust`,
    interval: `${training.rondes} intervallen · ${training.rustTijd}s rust`,
    race: "Race modus · Continue timer + splits",
  }[training.timerModus];

  return (
    <>
      <div className="min-h-screen bg-[#0a0a0a]">
        {/* Navbar */}
        <nav className="bg-[#0a0a0a] border-b border-white/5 px-4 sm:px-8 py-4 flex items-center justify-between">
          <button
            data-testid="hyrox-back-btn"
            onClick={() => navigate("/account")}
            className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
          >
            <ArrowLeft size={16} />
            <span className="text-sm">Terug</span>
          </button>
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="FitSmith" className="h-7 w-7 object-contain" />
            <span className="text-white font-bold text-base tracking-wide">FitSmith</span>
          </div>
          {user && (
            <button
              onClick={() => { logout(); navigate("/"); }}
              className="text-zinc-600 hover:text-zinc-300 transition-colors"
              title="Uitloggen"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16,17 21,12 16,7"/><line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
            </button>
          )}
        </nav>

        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">

          {/* Header */}
          <div className="mb-8">
            {/* Color accent bar */}
            <div className="w-12 h-1 rounded-full mb-4" style={{ background: training.kleur }} />

            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span
                className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full"
                style={{ background: training.kleurBg, color: training.kleur, border: `1px solid ${training.kleurBorder}` }}
              >
                Hyrox Training
              </span>
              <span
                className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full"
                style={{ background: `${training.niveauKleur}15`, color: training.niveauKleur, border: `1px solid ${training.niveauKleur}35` }}
              >
                {training.niveau}
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl font-black text-white mb-2">{training.naam}</h1>
            <p className="text-zinc-400 text-sm mb-4 leading-relaxed">{training.beschrijving}</p>

            {/* Stats row */}
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-1.5">
                <Target size={13} style={{ color: training.kleur }} />
                <span className="text-zinc-400 text-xs">{training.doel}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <RotateCcw size={13} style={{ color: training.kleur }} />
                <span className="text-zinc-400 text-xs">
                  {training.rondes === 1 ? "1 ronde" : `${training.rondes} rondes`}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <Clock size={13} style={{ color: training.kleur }} />
                <span className="text-zinc-400 text-xs">{timerModeLabel}</span>
              </div>
            </div>
          </div>

          {/* Timer CTA Card */}
          <div
            className="rounded-2xl p-5 mb-8 border flex items-center justify-between gap-4"
            style={{ background: `${training.kleur}08`, borderColor: `${training.kleurBorder}` }}
          >
            <div>
              <p className="text-white font-bold text-base mb-0.5">Hyrox Timer</p>
              <p className="text-zinc-500 text-xs">
                {training.timerModus === "race"
                  ? "Continue timer met split registratie per onderdeel"
                  : `Automatische rustimer van ${training.rustTijd} seconden tussen rondes`}
              </p>
            </div>
            <button
              data-testid="open-hyrox-timer-btn"
              onClick={() => setTimerOpen(true)}
              className="shrink-0 flex items-center gap-2 font-black text-sm text-black px-5 py-3 rounded-xl transition-transform active:scale-95"
              style={{ background: training.kleur }}
            >
              <Play size={15} fill="black" /> Start Timer
            </button>
          </div>

          {/* Exercise list */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs font-black uppercase tracking-widest text-zinc-500">
                Oefeningen — {training.exercises.length} onderdelen
              </p>
              {training.timerModus === "race" && (
                <span
                  className="text-xs font-bold px-2 py-0.5 rounded-full"
                  style={{ background: `${training.kleur}15`, color: training.kleur }}
                >
                  Split per onderdeel
                </span>
              )}
            </div>

            <div className="space-y-3">
              {training.exercises.map((ex, i) => (
                <HyroxExerciseCard key={ex.id} exercise={ex} index={i} kleur={training.kleur} />
              ))}
            </div>
          </div>

          {/* Timer button (bottom repeat) */}
          <div className="mt-8 pt-6 border-t border-white/5">
            <button
              data-testid="open-hyrox-timer-bottom-btn"
              onClick={() => setTimerOpen(true)}
              className="w-full py-4 rounded-2xl font-black text-base text-black flex items-center justify-center gap-3 transition-transform active:scale-95"
              style={{ background: training.kleur }}
            >
              <Timer size={18} /> Start Hyrox Timer
            </button>
            <p className="text-zinc-600 text-xs text-center mt-3">
              {training.timerModus === "race"
                ? "Race simulatie — continue timer met splits"
                : `${training.rondes} rondes · ${training.rustTijd}s automatische rusttimer`}
            </p>
          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-white/5 flex items-center justify-between">
            <p className="text-zinc-700 text-xs">FitSmith Academy — Hyrox Trainingen</p>
            <p className="text-zinc-700 text-xs">fitsmith.nl</p>
          </div>
        </div>
      </div>

      {/* Timer Overlay */}
      {timerOpen && (
        <HyroxTimerOverlay training={training} onClose={() => setTimerOpen(false)} />
      )}
    </>
  );
}
